import { useState } from "react";
import { Link } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Badge, Button, EmptyState, Icon, useToast } from "@agentsaas/ui";
import type { Agent } from "@agentsaas/protocol";
import { useErrorText, useT } from "../app/i18n";
import { useServices } from "../app/services";
import { qk } from "../data/queries";
import { AgentStatusDot, STATUS_TONE } from "../agent/TeamList";
import { DevPanel } from "../dev/DevPanel";
import { useTeamInfo } from "./useTeam";

/**
 * 团队面板：每个 agent 一张卡（状态、角色、模板版本、当前任务），可 reload 到最新模板；
 * 入口：新建 agent（可选自定义模板）、模板管理、agent 邮箱。桌面在项目首页主区，手机在"团队" tab。
 */
export function TeamPanel({ pid, agents }: { pid: string; agents: Agent[] }) {
  const { t } = useT();
  const { mock } = useServices();
  const info = useTeamInfo(pid);
  return (
    <div className="page team-panel" data-testid="team-panel">
      <div className="page__head">
        <h1 className="page__title">{t("team.title")}</h1>
        <div className="row">
          {info.tenantId ? (
            <Link className="ui-btn ui-btn--secondary ui-btn--md" to={`/t/${info.tenantId}/templates`} data-testid="templates-link">
              <Icon name="layers" />
              {t("template.title")}
            </Link>
          ) : null}
          <Link to={`/p/${pid}/new`} className="ui-btn ui-btn--primary ui-btn--md" data-testid="new-agent-main">
            <Icon name="plus" />
            {t("agent.new")}
          </Link>
        </div>
      </div>
      {agents.length ? (
        <ul className="team-cards">
          {agents.map((a) => (
            <AgentCard key={a.id} pid={pid} agent={a} info={info} />
          ))}
        </ul>
      ) : (
        <EmptyState icon="users" title={t("agent.empty")} />
      )}
      {mock ? <DevPanel pid={pid} /> : null}
    </div>
  );
}

function AgentCard({ pid, agent: a, info }: { pid: string; agent: Agent; info: ReturnType<typeof useTeamInfo> }) {
  const { t } = useT();
  const errText = useErrorText();
  const toast = useToast();
  const qc = useQueryClient();
  const { api } = useServices();
  const [busy, setBusy] = useState(false);
  const tpl = info.templateOf(a);
  const task = info.currentTask(a);
  const outdated = !!tpl && tpl.version > a.templateVersion;
  const reload = async () => {
    setBusy(true);
    try {
      await api.call("agentReload", { params: { aid: a.id } });
      toast.show({ message: t("team.reloaded", { name: a.name }), tone: "success", duration: 2500 });
      void qc.invalidateQueries({ queryKey: qk.agents(pid) });
    } catch (e) {
      toast.show({ message: errText(e), tone: "danger" });
    } finally {
      setBusy(false);
    }
  };
  return (
    <li className="card team-card" data-testid="team-card" data-agent={a.name}>
      <div className="team-card__head">
        <AgentStatusDot status={a.status} />
        <Link to={`/p/${pid}/a/${a.id}`} className="team-card__name ellipsis">
          {a.name}
        </Link>
        <Badge tone={STATUS_TONE[a.status]}>{t(`agent.status.${a.status}`)}</Badge>
      </div>
      <div className="muted small team-card__role">
        {info.roleOf(a)}
        <span className="sep" aria-hidden="true" />
        {t("team.template_version", { version: a.templateVersion })}
        {outdated ? (
          <Badge tone="warning" className="team-card__outdated">
            {t("team.template_updated", { version: tpl.version })}
          </Badge>
        ) : null}
      </div>
      <div className="team-card__task small">
        <span className="muted">{t("team.current_task")}</span>{" "}
        {task ? (
          <Link to={`/p/${pid}/tasks/${task.id}`} data-testid="team-card-task">
            {task.title}
          </Link>
        ) : (
          <span className="muted">{t("team.no_task")}</span>
        )}
      </div>
      <div className="team-card__actions">
        <Link to={`/p/${pid}/mail?agent=${a.id}`} className="ui-btn ui-btn--ghost ui-btn--sm">
          <Icon name="mail" />
          {t("team.mailbox")}
        </Link>
        <Link to={`/p/${pid}/a/${a.id}/capabilities`} className="ui-btn ui-btn--ghost ui-btn--sm" data-testid="agent-caps-link">
          <Icon name="sparkles" />
          {t("agentcap.tab")}
        </Link>
        <Button size="sm" variant={outdated ? "primary" : "secondary"} icon="refresh" loading={busy} onClick={() => void reload()} data-testid="agent-reload">
          {t("team.reload")}
        </Button>
      </div>
    </li>
  );
}
